import { AlertTriangle, Boxes, Layers, ListOrdered, Maximize2, Package, PackageOpen, Weight } from 'lucide-react';
import { fmtVolume, fmtWeight, qty } from '../lib/utils';
import { cn } from '../lib/utils';

/* Small informative tags shared by the order list, the order header and
   the line rows. Sentence case, one idea per tag, colour only when it
   asks for attention. */
const Tag = ({ tone = 'muted', icon: Icon, children, className }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] font-semibold leading-4',
      tone === 'muted' && 'bg-sunken text-ink2',
      tone === 'ink' && 'bg-ink text-bg',
      tone === 'hold' && 'tone-crossdock bg-tone-bg text-tone',
      tone === 'alert' && 'tone-over bg-tone-bg text-tone',
      tone === 'out' && 'tone-out bg-tone-bg text-tone',
      tone === 'stock' && 'tone-stock bg-tone-bg text-tone',
      className
    )}
  >
    {Icon && <Icon className="h-3.5 w-3.5" />}
    {children}
  </span>
);

export const SIZE = {
  small: { label: 'Small order', tone: 'muted', icon: Package },
  big: { label: 'Big order', tone: 'stock', icon: PackageOpen },
  huge: { label: 'Huge order', tone: 'hold', icon: Layers },
};

/* Why an order is big, one tag per reason with its number. Level big is
   informative (blue), very asks for room or hands (amber), exceptional is
   a heads-up before the truck arrives (red). */
const REASON = {
  volume: { icon: PackageOpen, word: { big: 'Bulky', very: 'Very bulky', exceptional: 'Exceptional volume' }, fmt: fmtVolume },
  weight: { icon: Weight, word: { big: 'Heavy', very: 'Very heavy', exceptional: 'Exceptional weight' }, fmt: fmtWeight },
  units: { icon: Boxes, word: { big: 'Many units', very: 'Lots of units', exceptional: 'Exceptional count' }, fmt: (v) => qty(v) },
  lines: { icon: ListOrdered, word: { big: 'Many lines', very: 'Lots of lines', exceptional: 'Exceptional spread' }, fmt: (v) => `${qty(v)} open` },
};
const LEVEL_TONE = { big: 'stock', very: 'hold', exceptional: 'alert' };

const LEVEL_RANK = { exceptional: 3, very: 2, big: 1 };

/** The strongest reasons first; `max` keeps a list row to a couple of tags. */
export function sizeReasonTags(sizeTags, max = Infinity) {
  const sorted = [...(sizeTags || [])].sort((a, b) => (LEVEL_RANK[b.level] || 0) - (LEVEL_RANK[a.level] || 0));
  return sorted.slice(0, max).map((s) => {
    const r = REASON[s.key];
    if (!r) return null;
    return (
      <Tag key={`size-${s.key}`} tone={LEVEL_TONE[s.level] || 'stock'} icon={r.icon}>
        {r.word[s.level] || r.word.big} · {r.fmt(s.value)}
      </Tag>
    );
  });
}

/** Tags for a purchase order (list row or header). */
export function OrderTags({ order, compact = false, className, maxSize = 2 }) {
  const tags = [];
  if (order.size_tags?.length) tags.push(...sizeReasonTags(order.size_tags, maxSize));
  if (order.pallets > 0) tags.push(<Tag key="pallets" tone="ink" icon={Layers}>{order.pallets} {order.pallets === 1 ? 'pallet' : 'pallets'} announced</Tag>);
  else if (order.packages > 0) tags.push(<Tag key="packages" tone="ink" icon={Package}>{order.packages} {order.packages === 1 ? 'parcel' : 'parcels'} announced</Tag>);
  if (order.pallet_lines > 0) tags.push(<Tag key="pl" tone="muted" icon={Layers}>{order.pallet_lines} on pallet</Tag>);
  if (order.fragile_lines > 0) tags.push(<Tag key="fr" tone="alert" icon={AlertTriangle}>{order.fragile_lines} fragile</Tag>);
  if (order.unsized_lines > 0) tags.push(<Tag key="ns" tone="hold">{order.unsized_lines} without packaging</Tag>);
  if (order.unconfirmed_lines > 0) tags.push(<Tag key="uc" tone="hold">{order.unconfirmed_lines} unconfirmed</Tag>);
  if (!tags.length) return null;
  return <span className={cn('flex flex-wrap gap-1', compact && 'mt-1', className)}>{tags}</span>;
}

/** What a line needs the operator to know, for the notice band of its card:
    handling first, then what is missing. Empty for a clean line. */
export function lineNotices(line) {
  const out = [];
  if (line.fragile) out.push({ key: 'fr', text: 'Fragile', icon: AlertTriangle, tone: 'alert' });
  if (line.pallet) out.push({ key: 'pl', text: 'On pallet', icon: Layers, tone: 'ink' });
  if (line.oversize) out.push({ key: 'os', text: 'Oversize', icon: Maximize2, tone: 'ink' });
  if (line.status !== 'done') {
    if (line.packaging_status === 'none') out.push({ key: 'pk', text: 'No packaging: tap to add' });
    else if (line.packaging_status === 'unconfirmed') out.push({ key: 'pk', text: 'Packaging to confirm' });
    if (line.no_vendor_ean || !line.scannable) out.push({ key: 'ean', text: line.scannable ? 'No vendor EAN: count by hand' : 'No barcode: count by hand' });
  }
  return out;
}

/** Tags for a line (product flags + packaging state). */
export function LineTags({ line, className }) {
  const tags = [];
  if (line.fragile) tags.push(<Tag key="fr" tone="alert" icon={AlertTriangle}>Fragile</Tag>);
  if (line.pallet) tags.push(<Tag key="pl" tone="ink" icon={Layers}>On pallet</Tag>);
  if (line.oversize) tags.push(<Tag key="os" tone="ink" icon={Maximize2}>Oversize</Tag>);
  if (line.status !== 'done') {
    if (line.packaging_status === 'none') tags.push(<Tag key="pk" tone="hold">No packaging: tap to add</Tag>);
    else if (line.packaging_status === 'unconfirmed') tags.push(<Tag key="pk" tone="hold">Packaging to confirm</Tag>);
    if (line.no_vendor_ean || !line.scannable) tags.push(<Tag key="ean" tone="hold">{line.scannable ? 'No vendor EAN: count by hand' : 'No barcode: count by hand'}</Tag>);
  }
  if (!tags.length) return null;
  return <span className={cn('flex flex-wrap gap-1', className)}>{tags}</span>;
}

export default Tag;
